import {
  Avatar,
  Box,
  Flex,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  Square,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { RouterOutputs } from '../lib/trpcAdmin';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { LucideFilter } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SortOrder } from '@duke-hq/lib/pagination';
import { RoleMenuItem } from '@duke-hq/app-admin-web/components/RoleMenuItem';
import { SortableHeader } from '@duke-hq/ui/components/SortableHeader/SortableHeader';
import { UserRoleSchema } from '@duke-hq/svc-api-db/schemas/enums/UserRole.schema';

import { AdminUserRoleSchema, AdminUserRole } from '@duke-hq/shared-constants/admin';
import { SortFilters, SortFiltersSchema } from './types';

import { djs } from '@duke-hq/djs';

interface UsersProps {
  users: RouterOutputs['users']['all'];
  onFilters: (filters: SortFilters) => void;
}

const roleColors: Record<AdminUserRole, string> = {
  [UserRoleSchema.enum.ADMIN]: 'blue.600',
  [UserRoleSchema.enum.ASSESSOR]: 'orange.600',
};

const availableRoles: AdminUserRole[] = [UserRoleSchema.enum.ADMIN, UserRoleSchema.enum.ASSESSOR];

export function AdminUsersTable({ users, onFilters }: UsersProps) {
  const items = users ?? [];
  const [sortDirection, setSortDirection] = useState<SortOrder | undefined>(SortOrder.ASC);
  const [orderBy, setOrderBy] = useState<string>('name');

  const [selectedRole, setSelectedRole] = useState<AdminUserRole | undefined>(undefined);
  const [filters, setFilters] = useState<SortFilters>({
    role: selectedRole ?? undefined,
    orderBy: undefined,
    sort: undefined,
  });

  const [searchParams] = useSearchParams();

  const navigate = useNavigate();

  const updateFilters = useCallback(
    (newOrderBy: string, newSortDirection: SortOrder) => {
      const updatedFilters = {
        role: SortFiltersSchema.safeParse({
          role: searchParams.get('role') ?? undefined,
        }).data?.role,
        orderBy: newOrderBy,
        sort: newSortDirection,
      };

      setFilters((prevFilters) => ({
        ...prevFilters,
        ...updatedFilters,
      }));

      onFilters({ ...updatedFilters });
    },
    [searchParams],
  );

  const handleSort = useCallback(
    (name: string) => {
      const newSortDirection = sortDirection === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC;
      setOrderBy(name);
      setSortDirection(newSortDirection);
      updateFilters(name, newSortDirection);
    },
    [updateFilters, setOrderBy, setSortDirection, sortDirection],
  );

  const handleRoleClick = useCallback(
    (role: string | undefined) => {
      const adminRole = AdminUserRoleSchema.safeParse(role).data;

      setSelectedRole(adminRole);
      setFilters((prevFilters) => ({
        ...prevFilters,
        role: adminRole,
      }));
      onFilters({ ...filters, role: adminRole });
    },
    [setSelectedRole, setFilters, onFilters, filters],
  );

  return (
    <Table>
      <Thead>
        <Tr>
          <Th>
            <SortableHeader
              label={t`User`}
              columnName="name"
              currentOrderBy={orderBy}
              currentSortOrder={sortDirection}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <Flex align={'center'}>
              <SortableHeader
                label={t`Role`}
                columnName="role"
                currentOrderBy={orderBy}
                currentSortOrder={sortDirection}
                onSortChange={handleSort}
              />
              <Menu>
                <MenuButton
                  aria-label={t`Filter by role`}
                  ml="auto"
                  as={IconButton}
                  variant={'ghost'}
                  color={selectedRole ? roleColors[selectedRole] : 'gray.500'}
                  icon={<Icon as={LucideFilter} />}
                ></MenuButton>
                <MenuList>
                  {availableRoles.map((role) => (
                    <RoleMenuItem
                      key={role}
                      role={role}
                      isSelected={selectedRole === role}
                      onRoleClick={handleRoleClick}
                    />
                  ))}
                </MenuList>
              </Menu>
            </Flex>
          </Th>
          <Th>
            <SortableHeader
              label={t`Created at`}
              columnName="created_at"
              currentOrderBy={orderBy}
              currentSortOrder={sortDirection}
              onSortChange={handleSort}
            />
          </Th>
          <Th>
            <SortableHeader
              label={t`Last login`}
              columnName="last_authenticated_at"
              currentOrderBy={orderBy}
              currentSortOrder={sortDirection}
              onSortChange={handleSort}
            />
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {items.map((user) => (
          <AdminUsersTableRow
            key={user.id}
            user={user}
            navigate={navigate}
            roleColors={roleColors}
          />
        ))}
      </Tbody>
    </Table>
  );
}

function AdminUsersTableRow({
  user,
  navigate,
  roleColors,
}: {
  user: RouterOutputs['users']['all'][number];
  navigate: ReturnType<typeof useNavigate>;
  roleColors: Record<AdminUserRole, string>;
}) {
  const userFullName = user.first_name + (user.last_name ? ' ' + user.last_name : '');

  const onClick = useCallback(() => {
    navigate({
      pathname: `/users/${user.id}/details`,
      search: location.search,
    });
  }, [navigate, user.id]);

  return (
    <Tr cursor={'pointer'} _hover={{ bg: 'bg.subtle' }} onClick={onClick}>
      <Td>
        <Flex>
          <Flex align="center" justify="center">
            <Box>
              <Square size="32px">
                <Avatar size="sm" name={userFullName} src={user?.picture_url ?? undefined} />
              </Square>
            </Box>
          </Flex>
          <Flex direction="column" ml={4}>
            <Box>
              <Text fontWeight="medium">{userFullName}</Text>
            </Box>
            <Flex align="center" color="fg.muted" gap={1}>
              <Text>{user.email}</Text>
            </Flex>
          </Flex>
        </Flex>
      </Td>
      <Td>
        <Tag borderRadius={'full'}>
          <Text fontSize={'xs'} color={roleColors[user.role]}>
            {user.role}
          </Text>
        </Tag>
      </Td>
      <Td>
        <Text color="fg.muted">{djs(user.created_at).format('dddd, DD MMMM YYYY')}</Text>
      </Td>
      <Td>
        <Text fontSize="sm" color="fg.muted">
          <Trans>Last seen {djs().to(djs(user.last_authenticated_at))}</Trans>
        </Text>
      </Td>
    </Tr>
  );
}
